import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { bucketPins } from '../utils/mapPinCluster';

const pricePinIcon = (label, isActive) =>
  L.divIcon({
    className: 'msrm-pin-root',
    html: `<div class="msrm-price-pin ${isActive ? 'msrm-price-pin--active' : ''}" title="${label}">${label}</div>`,
    iconSize: [88, 34],
    iconAnchor: [44, 34],
  });

const clusterIcon = (count) =>
  L.divIcon({
    className: 'msrm-cluster-root',
    html: `<div class="msrm-cluster-pin"><span>${count}</span><small>rooms</small></div>`,
    iconSize: [52, 52],
    iconAnchor: [26, 26],
  });

const MeetingMapPinCluster = ({ pins = [], selectedRoomId, onPinClick }) => {
  const map = useMap();
  const layerRef = useRef(null);

  const renderMarkers = useCallback(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    const grouped = bucketPins(pins, map.getZoom());
    const layer = L.layerGroup();

    grouped.forEach((entry) => {
      if (entry.type === 'pin') {
        const pin = entry.pin;
        const isActive = selectedRoomId && String(selectedRoomId) === String(pin.id);
        const marker = L.marker([pin.lat, pin.lng], {
          icon: pricePinIcon(pin.label, isActive),
          zIndexOffset: isActive ? 1000 : 0,
        });
        marker.bindPopup(`<strong>${pin.name}</strong><br/>${pin.label}`);
        marker.on('click', () => onPinClick?.(pin));
        layer.addLayer(marker);
        return;
      }

      const marker = L.marker([entry.lat, entry.lng], {
        icon: clusterIcon(entry.count),
        zIndexOffset: 200,
      });
      marker.on('click', () => {
        map.setView([entry.lat, entry.lng], Math.min(map.getZoom() + 2, 16));
      });
      layer.addLayer(marker);
    });

    layer.addTo(map);
    layerRef.current = layer;
  }, [map, pins, selectedRoomId, onPinClick]);

  useEffect(() => {
    renderMarkers();
    map.on('zoomend', renderMarkers);
    return () => {
      map.off('zoomend', renderMarkers);
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, renderMarkers]);

  return null;
};

export default MeetingMapPinCluster;
